import { Suspense, lazy } from 'react';
import AuthGuard from './components/AuthGuard';
import Loading from './components/Loading';
import MainLayout from './components/MainLayout';

const Loadable = (Component) => (props) =>
  (
    <Suspense fallback={<Loading />}>
      <Component {...props} />
    </Suspense>
  );

// Authentication pages
const Login = Loadable(lazy(() => import('./pages/authentication/Login')));
const Logout = Loadable(lazy(() => import('./pages/authentication/Logout')));
const PasswordRecovery = Loadable(
  lazy(() => import('./pages/authentication/PasswordRecovery'))
);
const PasswordReset = Loadable(
  lazy(() => import('./pages/authentication/PasswordReset'))
);
const Register = Loadable(
  lazy(() => import('./pages/authentication/Register'))
);

// Account pages
const Account = Loadable(lazy(() => import('./pages/account')));

// Training Plan Pages
const TrainingPlanDetails = Loadable(
  lazy(() => import('./pages/training_plans/TrainingPlanDetails'))
);
const TrainingPlanList = Loadable(
  lazy(() => import('./pages/training_plans/TrainingPlanList'))
);

// Workout Pages
const WorkoutDetails = Loadable(
  lazy(() => import('./pages/workouts/WorkoutDetails'))
);

const WorkoutList = Loadable(
  lazy(() => import('./pages/workouts/WorkoutList'))
);

// Exercise Pages
const ExerciseDetails = Loadable(
  lazy(() => import('./pages/exercises/ExerciseDetails'))
);

const ExerciseList = Loadable(
  lazy(() => import('./pages/exercises/ExerciseList'))
);

// Workout SessionPages
const WorkoutSessionDetails = Loadable(
  lazy(() => import('./pages/workout_sessions/WorkoutSessionDetails'))
);
const WorkoutSessionNew = Loadable(
  lazy(() => import('./pages/workout_sessions/WorkoutSessionNew'))
);
const WorkoutSessionList = Loadable(
  lazy(() => import('./pages/workout_sessions/WorkoutSessionList'))
);

// Posts
const PostNew = Loadable(lazy(() => import('./pages/posts/PostNew')));
const PostDetails = Loadable(lazy(() => import('./pages/posts/PostDetails')));
const PostList = Loadable(lazy(() => import('./pages/posts/PostList')));

// Meals
const MealDetails = Loadable(lazy(() => import('./pages/meals/MealDetails')));
const MealList = Loadable(lazy(() => import('./pages/meals/MealList')));

// Logged in Dashboard
const Dashboard = Loadable(lazy(() => import('./pages/dashboard/index')));

// Trainer Pages
const AppDownload = Loadable(lazy(() => import('./pages/AppDownload')));
const About = Loadable(lazy(() => import('./pages/About')));
const Contact = Loadable(lazy(() => import('./pages/Contact')));

// Checkout
const DeprecatedCheckout = Loadable(
  lazy(() => import('./pages/checkout/DeprecatedCheckout'))
);
const Checkout = Loadable(lazy(() => import('./pages/checkout/Checkout')));
const CheckoutSuccess = Loadable(
  lazy(() => import('./pages/checkout/Success'))
);
const CheckoutCanceled = Loadable(
  lazy(() => import('./pages/checkout/Canceled'))
);

// Other pages
const TrainerLanding = Loadable(lazy(() => import('./pages/TrainerLanding')));
const PrivacyPolicy = Loadable(
  lazy(() => import('./pages/legal/PrivacyPolicy'))
);
const TermsOfService = Loadable(
  lazy(() => import('./pages/legal/TermsOfService'))
);

// Error pages

const AuthorizationRequired = Loadable(
  lazy(() => import('./pages/AuthorizationRequired'))
);
const NotFound = Loadable(lazy(() => import('./pages/NotFound')));
const ServerError = Loadable(lazy(() => import('./pages/ServerError')));

const routes = [

  {
    path: 'logout',
    element: <Logout />,
  },
  {
    path: 'forgot_password',
    element: <PasswordRecovery />,
  },
  {
    path: 'reset_password',
    element: <PasswordReset />,
  },
  {
    path: '/account/reset_password',
    element: <PasswordReset />,
  },
  {
    path: '/:trainer_id/checkout',
    element: <Checkout />,
  },
  {
    path: 'checkout',
    element: <Checkout />,
  },
  {
    path: '*',
    element: <MainLayout />,
    children: [
      {
        path: '/',
        element: <TrainerLanding />,
      },
      {
        path: 'privacy_policy',
        element: <PrivacyPolicy />,
      },
      {
        path: 'terms_of_service',
        element: <TermsOfService />,
      },
      {
        path: '401',
        element: <AuthorizationRequired />,
      },
      {
        path: '404',
        element: <NotFound />,
      },
      {
        path: '500',
        element: <ServerError />,
      },
      {
        path: 'login',
        element: <Login />,
      },
      {
        path: 'signup',
        element: <Register />,
      },
      {
        path: 'account',
        element: (
          <AuthGuard>
            <Account />
          </AuthGuard>
        ),
      },
      {
        path: 'dashboard',
        element: (
          <AuthGuard>
            <Dashboard />
          </AuthGuard>
        ),
      },
      {
        path: 'checkout/success',
        element: <CheckoutSuccess />,
      },
      {
        path: 'checkout/canceled',
        element: <CheckoutCanceled />,
      },
      {
        path: 'app_download',
        element: <AppDownload />,
      },
      {
        path: 'workouts/:id',
        element: <WorkoutDetails />,
      },
      {
        path: 'workouts',
        element: <WorkoutList />,
      },
      {
        path: 'exercises/:id',
        element: <ExerciseDetails />,
      },
      {
        path: 'exercises',
        element: <ExerciseList />,
      },
      {
        path: 'posts/new',
        element: <PostNew />,
      },
      {
        path: 'posts/:id',
        element: <PostDetails />,
      },
      {
        path: 'posts',
        element: <PostList />,
      },
      {
        path: 'workout_sessions/new',
        element: <WorkoutSessionNew />,
      },
      {
        path: 'workout_sessions/:id',
        element: <WorkoutSessionDetails />,
      },
      {
        path: 'workout_sessions',
        element: <WorkoutSessionList />,
      },
      {
        path: 'training_plans/:id',
        element: <TrainingPlanDetails />,
      },
      {
        path: 'training_plans',
        element: <TrainingPlanList />,
      },
      {
        path: 'meals/:id',
        element: <MealDetails />,
      },
      {
        path: 'meals',
        element: <MealList />,
      },
      {
        path: 'about',
        element: <About />,
      },
      {
        path: 'contact',
        element: <Contact />,
      },

      // @deprecated
      {
        path: 'checkout/:slug', // deprecated, older payments use this link
        element: <DeprecatedCheckout />,
      },
      // @deprecated
      {
        path: 'trainer/:trainer_id/terms_of_service', // deprecated, older payments use this link
        element: <TermsOfService />,
      },
      // @deprecated
      {
        path: 'trainer/:trainer_id/privacy_policy', // deprecated
        element: <PrivacyPolicy />,
      },

      // for trainers without custom domains
      {
        path: '/:trainer_id',
        element: <TrainerLanding />,
      },
      {
        path: '/:trainer_id/login',
        element: <Login />,
      },
      {
        path: '/:trainer_id/signup',
        element: <Register />,
      },
      {
        path: '/:trainer_id/account',
        element: (
          <AuthGuard>
            <Account />
          </AuthGuard>
        ),
      },
      {
        path: '/:trainer_id/dashboard',
        element: (
          <AuthGuard>
            <Dashboard />
          </AuthGuard>
        ),
      },
      {
        path: '/:trainer_id/checkout/success',
        element: <CheckoutSuccess />,
      },
      {
        path: '/:trainer_id/checkout/canceled',
        element: <CheckoutCanceled />,
      },
      {
        path: '/:trainer_id/app_download',
        element: <AppDownload />,
      },
      {
        path: '/:trainer_id/workouts/:id',
        element: <WorkoutDetails />,
      },
      {
        path: '/:trainer_id/workouts',
        element: <WorkoutList />,
      },
      {
        path: '/:trainer_id/exercises/:id',
        element: <ExerciseDetails />,
      },
      {
        path: '/:trainer_id/exercises',
        element: <ExerciseList />,
      },

      {
        path: '/:trainer_id/posts/new',
        element: <PostNew />,
      },
      {
        path: '/:trainer_id/posts/:id',
        element: <PostDetails />,
      },
      {
        path: '/:trainer_id/posts',
        element: <PostList />,
      },
      {
        path: '/:trainer_id/workout_sessions/new',
        element: <WorkoutSessionNew />,
      },
      {
        path: '/:trainer_id/workout_sessions/:id',
        element: <WorkoutSessionDetails />,
      },
      {
        path: '/:trainer_id/workout_sessions',
        element: <WorkoutSessionList />,
      },
      {
        path: '/:trainer_id/training_plans/:id',
        element: <TrainingPlanDetails />,
      },
      {
        path: '/:trainer_id/training_plans',
        element: <TrainingPlanList />,
      },
      {
        path: '/:trainer_id/meals/:id',
        element: <MealDetails />,
      },
      {
        path: '/:trainer_id/meals',
        element: <MealList />,
      },
      {
        path: '/:trainer_id/about',
        element: <About />,
      },
      {
        path: '/:trainer_id/contact',
        element: <Contact />,
      },
      {
        path: '/:trainer_id/privacy_policy',
        element: <PrivacyPolicy />,
      },
      {
        path: '/:trainer_id/terms_of_service',
        element: <TermsOfService />,
      },
      {
        path: '/:trainer_id/forgot_password',
        element: <PasswordRecovery />,
      },
      {
        path: '/:trainer_id/reset_password',
        element: <PasswordReset />,
      },
      // @deprecated
      {
        path: 'checkout/:slug', // deprecated, older payments use this link
        element: <DeprecatedCheckout />,
      },
      // @deprecated
      {
        path: 'trainer/:trainer_id/terms_of_service', // deprecated, older payments use this link
        element: <TermsOfService />,
      },
      // @deprecated
      {
        path: 'trainer/:trainer_id/privacy_policy', // deprecated
        element: <PrivacyPolicy />,
      },
      {
        path: '*',
        element: <NotFound />,
      },
    ],
  },
];

export default routes;
